import type { RegisterUserRequest } from '@contracts/users/RegisterUserRequest';
import type { UpdateUserRequest } from '@contracts/users/UpdateUserRequest';
import type { UserListResponse } from '@contracts/users/UserListResponse';
import type { UserPermission } from '@contracts/users/UserPermission';
import type { UserResponse } from '@contracts/users/UserResponse';
import { AuthenticatedClient } from '@src/common/services/AuthenticatedClient';
import { User } from '@src/manageUsers/domain/User';
import { UserList } from '@src/manageUsers/domain/UserList';

export const UsersClient = {
  list: async (): Promise<UserList> => {
    const { data: response } =
      await AuthenticatedClient.get<UserListResponse>('users');

    return new UserList(
      response.users.map(
        (userDto) => new User({ ...userDto, permissions: new Set() }),
      ),
    );
  },

  create: async (data: { name: string; email: string; extension?: string }) => {
    await AuthenticatedClient.post<undefined, undefined, RegisterUserRequest>(
      'users',
      data,
    );
  },

  update: async (
    userId: string,
    data: {
      name: string;
      extension?: string;
      permissions: Set<UserPermission>;
    },
  ) => {
    await AuthenticatedClient.put<undefined, undefined, UpdateUserRequest>(
      `/users/${userId}`,
      { ...data, permissions: [...data.permissions] },
    );
  },

  deactivate: async (userId: string) => {
    await AuthenticatedClient.delete(`/users/${userId}`);
  },

  activate: async (userId: string) => {
    await AuthenticatedClient.patch(`/users/${userId}:activate`);
  },

  confirmCognitoUser: async (userId: string) => {
    await AuthenticatedClient.patch(`/users/${userId}:confirm-cognito`);
  },

  find: async (userId: string) => {
    const { data: response } = await AuthenticatedClient.get<UserResponse>(
      `/users/${userId}`,
    );

    return new User({
      id: response.id,
      name: response.name,
      email: response.email,
      extension: response.extension,
      isInactive: response.isInactive,
      cognitoStatus: response.cognitoStatus,
      permissions: new Set(response.permissions),
    });
  },
};
